import { Injectable } from '@angular/core';
import { OrderformOrderState, PAYMENT_ERROR_MESSAGES } from '../../../ngrx/orderform.models';

@Injectable({
  providedIn: 'root',
})
export class CustomErrorMessageService {
  public getCustomErrorMessageFromOrderData(result: any, orderData: OrderformOrderState): string {
    const paymentErrorMessagesMapping = orderData?.order?.payment_provider?.payment_error_messages_mapping;
    if (!paymentErrorMessagesMapping) {
      return this._getDefaultOrOriginalMessage(result);
    }

    const errorCode = this._extractErrorCode(result);
    if (!errorCode) {
      return this._getDefaultOrOriginalMessage(result);
    }

    if (paymentErrorMessagesMapping.hasOwnProperty(errorCode)) {
      const paymentErrorIdentifier = paymentErrorMessagesMapping[errorCode];
      const improvedErrorMessage = this._translateErrorMessage(paymentErrorIdentifier);

      if (improvedErrorMessage && improvedErrorMessage.trim() !== '') {
        return improvedErrorMessage;
      }
    }

    return this._getDefaultOrOriginalMessage(result);
  }

  private _extractErrorCode(result: any): string | null {
    if (!result || !result.error) {
      return null;
    }

    const possibleCodeProps = [
      'decline_code', // Stripe decline code
      'code',
      'error_code',
      'errorCode',
    ];

    for (const prop of possibleCodeProps) {
      if (result.error[prop]) {
        return result.error[prop];
      }
    }

    if (typeof result.error === 'string' || typeof result.error === 'number') {
      return result.error.toString();
    }

    return null;
  }

  private _getDefaultOrOriginalMessage(result: any): string {
    if (result?.error?.message) {
      return result.error.message;
    }

    return $localize`:@@payment_method.error.generic:The payment could not be processed at this time. Please try again with another payment method.`;
  }

  private _translateErrorMessage(paymentErrorIdentifier: string) {
    return (
      PAYMENT_ERROR_MESSAGES[paymentErrorIdentifier] ||
      $localize`:@@payment_method.error.generic:The payment could not be processed at this time. Please try again with another payment method.`
    );
  }

  public getCustomErrorMessageFromPrimer(error: string): string {
    switch (error) {
      case 'ERROR':
      case 'INVALID_CARD_NUMBER':
      case 'UNKNOWN':
      case 'ISSUER_TEMPORARILY_UNAVAILABLE':
      case 'WITHDRAWAL_LIMIT_EXCEEDED':
        return $localize`:@@payment_method_error.generic_decline:Card issuer declined payment. Please contact bank or try another payment method.`;

      case 'DO_NOT_HONOR':
        return $localize`:@@payment_method_error.do_not_honor:Card issuer declined payment. Please contact bank or try another payment method.`;

      case 'EXPIRED_CARD':
        return $localize`:@@payment_method_error.expired_card:Card expired. Please update details or use another payment method.`;

      case 'LOST_OR_STOLEN_CARD':
        return $localize`:@@payment_method_error.card_stolen:Card reported lost or stolen. Please contact bank or use another payment method.`;

      case 'SUSPECTED_FRAUD':
        return $localize`:@@payment_method_error.fraud_suspected:Bank blocked charge as it seemed unusual. Please contact bank or try another payment method.`;

      case 'DECLINED':
        return $localize`:@@payment_method_error.generic_decline:Card issuer declined payment. Please contact bank or try another payment method.`;

      case 'REFER_TO_CARD_ISSUER':
        return $localize`:@@payment_method_error.call_issuer:Card issuer declined payment. Please contact bank or try another payment method.`;

      case 'INSUFFICIENT_FUNDS':
        return $localize`:@@payment_method_error.insufficient_funds:Insufficient funds. Please check balance or try another payment method.`;

      case 'AUTHENTICATION_REQUIRED':
        return $localize`:@@payment_method_error.authentication_required:Bank needs to verify it's you. Please follow their steps to confirm or try another method.`;

      default:
        // Fall back to the default error message
        return $localize`:@@payment_method_error.generic_decline:Card issuer declined payment. Please contact bank or try another payment method.`;
    }
  }
}
